// Commented out along with the term estimator further down this file (see the explanation there).
// import { useState } from 'react'
// import clamp from 'lodash/clamp'

// import { toValidNumber } from '@ztechapps/common.utils' — commented out along with the term estimator below
import { formatNumber } from '@ztechapps/common.utils'

import { sectionAnchors } from '../../../config/routes'
import { appSettings } from '../../../config/settings'

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path d="M5 12l5 5L20 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

// Used when the env var is absent or not a positive number, so a missing/bad
// config never renders ₱NaN or ₱0.00.
const DEFAULT_PESO_PER_SHEET = 0.2
// A pack's credit count equals its peso price, because credits are seeded at price = credits.
// COUPLING: names/credits/order must match CREDIT_PACKAGES in
// apps/quickscore/api/src/migration/scripts/2026-07-24-seed-credit-packages-migration.ts
// const CREDIT_PACK_CREDITS = [100, 150, 200, 250, 300, 500]
// COUPLING: peso price must match the '100 credits' row's price in
// apps/quickscore/api/src/migration/scripts/2026-07-24-seed-credit-packages-migration.ts
const CLASS_PACK_PESO_PRICE = 100

// const clampWholeNumber = (value: string): number => Math.floor(clamp(toValidNumber(value), 1, 999))

// Credits/pricing tiers, ported verbatim from the prototype's .price-grid
// markup. Prices are subject to change without prior notice (see .ph note).
export const PricingSection = () => {
  const { pesoPerSheet } = appSettings
  const rate = pesoPerSheet && pesoPerSheet > 0 ? pesoPerSheet : DEFAULT_PESO_PER_SHEET

  // const [classes, setClasses] = useState('3')
  // const [students, setStudents] = useState('40')
  // const [tests, setTests] = useState('4')

  // const sheets = clampWholeNumber(classes) * clampWholeNumber(students) * clampWholeNumber(tests)
  // const total = sheets * rate
  // const pack = CREDIT_PACK_CREDITS.find((credits) => credits >= total)
  // The Class Pack's advertised sheet count is its price worth of scanning at the rate in force.
  const classPackSheets = Math.round(CLASS_PACK_PESO_PRICE / rate)

  return (
    <section id={sectionAnchors.pricing.slice(1)} className="band">
      <div className="wrap">
        <div className="sec-head">
          <span className="eyebrow">Pricing</span>
          <h2>Pay for what you scan, in credits</h2>
          <p>
            {`Credits are used up as you scan — about ₱${formatNumber(rate, '0,0.00')} a sheet. `}
            Buy a pack, scan whenever you need — nothing expires mid-term.
          </p>
        </div>
        <div className="price-grid">
          <div className="price">
            <h3>Starter</h3>
            <div className="amt">
              ₱0<small> / to try</small>
            </div>
            <p className="price-desc">Free credits to grade your first class and see the results.</p>
            <ul>
              <li>
                <CheckIcon />
                Free starter credits
              </li>
              <li>
                <CheckIcon />
                All question types
              </li>
              <li>
                <CheckIcon />
                CSV export
              </li>
            </ul>
            <a className="btn btn-ghost" href={sectionAnchors.contact}>
              Start free
            </a>
          </div>
          <div className="price featured">
            <span className="tag">Most popular</span>
            <h3>Class Pack</h3>
            <div className="amt">
              ₱{CLASS_PACK_PESO_PRICE}
              <small> / credit pack</small>
            </div>
            <p className="price-desc">
              A term&apos;s worth of scanning for a busy classroom — about {formatNumber(classPackSheets, '0,0')}{' '}
              sheets.
            </p>
            <ul>
              <li>
                <CheckIcon />
                Everything in Starter, plus:
              </li>
              <li>
                <CheckIcon />
                Usage history &amp; balance
              </li>
              <li>
                <CheckIcon />
                Item analysis reports
              </li>
            </ul>
          </div>
          <div className="price">
            <h3>School</h3>
            <div className="amt">Custom</div>
            <p className="price-desc">One shared credit balance for the whole school — department- or campus-wide.</p>
            <ul>
              <li>
                <CheckIcon />
                One pooled balance for every teacher
              </li>
              <li>
                <CheckIcon />
                Any teacher can top up the pool
              </li>
              <li>
                <CheckIcon />
                Each teacher&apos;s assessments stay private
              </li>
            </ul>
            <a className="btn btn-ghost" href={sectionAnchors.contact}>
              Talk to us
            </a>
          </div>
        </div>
        {/*
          The term estimator below is hidden as a live pricing-page experiment: testing whether a pricing
          section without an upfront cost estimate attracts more sign-ups than one showing it. The code stays
          in place, intact, so the calculator can be switched back on with one edit once the experiment has
          a result.

        <div className="estimator">
          <div>
            <h3>Estimate your term</h3>
            <div className="estimator-inputs">
              <div className="field">
                <label htmlFor="est-classes">Classes</label>
                <input
                  id="est-classes"
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  aria-describedby="est-hint"
                  value={classes}
                  onChange={(event) => setClasses(event.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="est-students">Students per class</label>
                <input
                  id="est-students"
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  aria-describedby="est-hint"
                  value={students}
                  onChange={(event) => setStudents(event.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="est-tests">Tests per term</label>
                <input
                  id="est-tests"
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  aria-describedby="est-hint"
                  value={tests}
                  onChange={(event) => setTests(event.target.value)}
                />
              </div>
            </div>
            <p className="estimator-hint" id="est-hint">
              The estimate updates as you type.
            </p>
          </div>
          <div className="estimator-result" aria-live="polite" aria-atomic="true">
            <p className="estimator-sheets">{`${formatNumber(sheets, '0,0')} sheets this term`}</p>
            <p className="estimator-total">{`₱${formatNumber(total, '0,0.00')}`}</p>
            <p className="estimator-rate">{`at ₱${formatNumber(rate, '0,0.00')} per sheet`}</p>
            <p className="estimator-pack">
              {pack !== undefined ? (
                `A ₱${pack} credit pack covers this term.`
              ) : (
                <>
                  No single pack covers this — <a href={sectionAnchors.contact}>talk to us about a School plan</a>.
                </>
              )}
            </p>
            <p className="estimator-note">
              Estimate only. Actual credits used depend on the length and content of each assessment — a denser or
              messier sheet can consume more.
            </p>
          </div>
        </div>
        */}
        <p className="ph">Prices are subject to change without prior notice.</p>
      </div>
    </section>
  )
}
